import { callLambda, type LambdaResponse } from './lambda.js';

// The public ledger accounts public-ledger-read-balance reads: nodeStream's own (PLATFORM, the PUBLIC_LEDGER team only),
// and a company's main account (COMPANY) and the operating account that pays its fees (OPERATING)
export type LedgerBalanceRole = 'PLATFORM' | 'COMPANY' | 'OPERATING';

export interface ReadBalanceRequest {
  companyId: string;
  roles: LedgerBalanceRole[];
  // Read the ledger even when the stored reading is under 30 s old
  fresh?: boolean;
}

// The message a lambda answered with when it refused, or the thrown error's
export function lambdaRefusal(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message;
  if (typeof err === 'string' && err) return err;
  return fallback;
}

function answer(result: LambdaResponse, fallback: string): unknown {
  if (result.res.code !== 200) {
    throw new Error(typeof result.res.message === 'string' && result.res.message ? result.res.message : fallback);
  }
  return result.res.message;
}

/**
 * Reads balances now through public-ledger-read-balance. The lambda answers with the readings and has
 * public-ledger-liquidity-check store them, so the stored ones on the company catch up a few seconds later.
 */
export async function readLedgerBalance(token: string | undefined, request: ReadBalanceRequest): Promise<unknown> {
  const result = await callLambda('public-ledger/read-balance', 'POST', token, JSON.stringify(request));
  return answer(result, 'Unable to read the public ledger balance');
}

/**
 * Confirms the PUBLIC_LEDGER team's top-up of nodeStream's platform account through public-ledger-confirm-platform-top-up,
 * which checks the entered total against the ledger and completes the job only once the account has reached its target.
 * A refusal throws with the lambda's message.
 */
export async function confirmPlatformTopUp(
  token: string | undefined,
  request: { jobId: string; enteredTotalCredits: number },
): Promise<unknown> {
  const result = await callLambda('public-ledger/confirm-platform-top-up', 'POST', token, JSON.stringify(request));
  return answer(result, 'Unable to confirm the top-up');
}
